import * as dateUtil from './dateUtil';

const DEFAULT_TIME = '09:00';

// 숫자를 날짜·시간 문자열에 사용할 두 자리 값으로 맞춘다.
function pad(value) {
    return String(value).padStart(2, '0');
}

// 브라우저별 문자열 Date 파싱 차이를 피하기 위해 날짜만 로컬 Date로 직접 만든다.
function toLocalDate(dateValue) {
    if (dateValue instanceof Date) {
        return new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate());
    }

    const [year, month, day] = String(dateValue).slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day);
}

// 날짜를 화면과 API 내부에서 공통으로 쓰는 YYYY-MM-DD 형식으로 변환한다.
export function formatDate(dateValue) {
    const date = toLocalDate(dateValue);
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// 날짜와 시각을 프론트 스케줄 상태 형식(YYYY-MM-DD HH:mm)으로 조합한다.
export function formatDateTime(dateValue, time = DEFAULT_TIME) {
    return `${formatDate(dateValue)} ${time}`;
}

// 일시 문자열에서 날짜 부분만 추출한다.
export function getDatePart(dateTime) {
    return String(dateTime || '').slice(0, 10);
}

// YYYY-MM-DD HH:mm 문자열을 로컬 기준 Date로 변환한다.
function toLocalDateTime(dateTime) {
    const [datePart, timePart = DEFAULT_TIME] = String(dateTime || '').split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);
    return new Date(year, month - 1, day, hour, minute);
}

// 로컬 Date를 프론트 스케줄 상태 형식(YYYY-MM-DD HH:mm)으로 변환한다.
function formatLocalDateTime(date) {
    return `${formatDate(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// 프론트 스케줄 시간을 API 요청 형식(초 포함)으로 변환한다.
export function toApiDateTime(dateTime) {
    if (!dateTime) return null;

    const [date, time = DEFAULT_TIME] = String(dateTime).split(' ');
    const [hour = '09', minute = '00'] = time.split(':');
    return `${date} ${pad(hour)}:${pad(minute)}:00`;
}

// 영업일 여부와 무관하게 달력 기준으로 날짜를 더하거나 뺀다.
export function addCalendarDays(dateValue, days) {
    const date = toLocalDate(dateValue);
    date.setDate(date.getDate() + days);
    return formatDate(date);
}

// 일시를 분 단위로 이동한다. 발송 10분 전 마감 시각 계산에 사용한다.
export function addMinutesToDateTime(dateTime, minutes) {
    const date = toLocalDateTime(dateTime);
    date.setMinutes(date.getMinutes() + minutes);
    return formatLocalDateTime(date);
}

// 두 일시의 차이를 밀리초로 반환한다. 음수면 left가 더 이른 시간이다.
export function compareDateTimes(left, right) {
    return toLocalDateTime(left).getTime() - toLocalDateTime(right).getTime();
}

// 모아서 보내기의 수집 종료 가능 마감 시각(발송 10분 전)을 반환한다.
export function getCollectionDeadline(collSndDate) {
    return addMinutesToDateTime(collSndDate, -10);
}

// 수집 기간 최소 10분 및 발송 10분 전 종료 규칙에 맞게 일정을 자동 보정한다.
function normalizeCollectionSchedule(schedule) {
    const nextSchedule = { ...schedule };
    const collectionDeadline = getCollectionDeadline(nextSchedule.collSndDate);

    if (compareDateTimes(nextSchedule.collEndDate, collectionDeadline) > 0) {
        nextSchedule.collEndDate = collectionDeadline;
    }

    const minimumCollEndDate = addMinutesToDateTime(nextSchedule.collStartDate, 10);
    if (compareDateTimes(nextSchedule.collEndDate, minimumCollEndDate) < 0) {
        if (compareDateTimes(minimumCollEndDate, collectionDeadline) <= 0) {
            nextSchedule.collEndDate = minimumCollEndDate;
        } else {
            nextSchedule.collEndDate = collectionDeadline;
            nextSchedule.collStartDate = addMinutesToDateTime(collectionDeadline, -10);
        }
    }

    return nextSchedule;
}

// 주말 또는 등록된 공휴일인지 확인한다.
export function isNonBusinessDay(dateValue) {
    const date = toLocalDate(dateValue);
    return dateUtil.isWeekend(date) || dateUtil.isHoliday(date);
}

// 주말·공휴일을 제외한 영업일 기준으로 날짜를 더한다.
export function addBusinessDays(dateValue, days) {
    const date = toLocalDate(dateValue);
    let addedDays = 0;

    while (addedDays < days) {
        date.setDate(date.getDate() + 1);
        if (!dateUtil.isWeekend(date) && !dateUtil.isHoliday(date)) {
            addedDays += 1;
        }
    }

    return formatDate(date);
}

// 추천 메시지의 기본 발송·수집 일정을 생성한다. ATS +2, 수집 +4/+5 영업일 정책이다.
export function createRecommendDefaultSchedule(now = new Date()) {
    return {
        targetType: 'ATS',
        mapticsSendType: 'maptics_collect',
        sendDate: formatDateTime(addBusinessDays(now, 2)),
        collStartDate: formatDateTime(addBusinessDays(now, 4)),
        collEndDate: formatDateTime(addBusinessDays(now, 4), '10:00'),
        collSndDate: formatDateTime(addBusinessDays(now, 5)),
        rtEndHour: '09',
        rtEndMinute: '00',
    };
}

// 추천 메시지 화면의 각 일정 입력값에 적용할 최소·최대 범위를 계산한다.
export function getRecommendScheduleLimits(schedule, now = new Date()) {
    const collectionDeadline = getCollectionDeadline(schedule.collSndDate);

    return {
        sendDate: { minDate: addBusinessDays(now, 2) },
        collSndDate: { minDate: addBusinessDays(now, 5) },
        collStartDate: { minDate: addBusinessDays(now, 4), maxDate: getDatePart(collectionDeadline), maxDateTime: collectionDeadline },
        collEndDate: { minDate: addBusinessDays(now, 4), maxDate: getDatePart(collectionDeadline), maxDateTime: collectionDeadline },
    };
}

// 추천 메시지에서 일정 관련 필드를 변경하고 필요한 연관 일정을 보정한다.
export function applyRecommendScheduleChange(schedule, field, value, now = new Date()) {
    const nextSchedule = { ...schedule };

    switch (field) {
        case 'targetType':
            return {
                ...createRecommendDefaultSchedule(now),
                targetType: value,
                mapticsSendType: schedule.mapticsSendType,
            };
        case 'mapticsSendType':
            nextSchedule.mapticsSendType = value;
            return nextSchedule;
        case 'sendDate':
            nextSchedule[field] = value;
            return nextSchedule;
        case 'collEndDate':
            nextSchedule.collEndDate = value;
            return schedule.mapticsSendType === 'maptics_collect'
                ? normalizeCollectionSchedule(nextSchedule)
                : nextSchedule;
        case 'collSndDate': {
            nextSchedule.collSndDate = value;
            return normalizeCollectionSchedule(nextSchedule);
        }
        case 'collStartDate':
            nextSchedule.collStartDate = value;
            if (schedule.mapticsSendType === 'maptics_collect') {
                return normalizeCollectionSchedule(nextSchedule);
            }
            if (schedule.mapticsSendType === 'maptics_realtime') {
                nextSchedule.collEndDate = value;
            }
            return nextSchedule;
        case 'rtEndHour':
            nextSchedule.rtEndHour = value;
            nextSchedule.collEndDate = `${getDatePart(schedule.collStartDate)} ${value}:${schedule.rtEndMinute}`;
            return nextSchedule;
        case 'rtEndMinute':
            nextSchedule.rtEndMinute = value;
            nextSchedule.collEndDate = `${getDatePart(schedule.collStartDate)} ${schedule.rtEndHour}:${value}`;
            return nextSchedule;
        default:
            return nextSchedule;
    }
}

// 셀프 메시지 기본 일정을 생성한다. 일반 ATS는 +5, 리마인드 ATS는 +3 영업일이다.
export function createSelfDefaultSchedule({
    isRemind = false,
    targetType = 'ATS',
    mapticsSendType = 'maptics_collect',
    now = new Date(),
} = {}) {
    const atsBusinessDays = isRemind ? 3 : 5;

    return {
        targetType,
        mapticsSendType,
        atsSndStartDate: formatDateTime(addBusinessDays(now, atsBusinessDays)),
        collStartDate: formatDateTime(addBusinessDays(now, 4)),
        collEndDate: formatDateTime(addBusinessDays(now, 4), '10:00'),
        collSndDate: formatDateTime(addBusinessDays(now, 5)),
        rtEndHour: '09',
        rtEndMinute: '00',
    };
}

// 셀프 메시지 화면의 각 일정 입력값에 적용할 최소·최대 범위를 계산한다.
export function getSelfScheduleLimits(schedule, { isRemind = false, now = new Date() } = {}) {
    const collectionDeadline = getCollectionDeadline(schedule.collSndDate);
    const atsBusinessDays = isRemind ? 3 : 5;

    return {
        atsSndStartDate: { minDate: addBusinessDays(now, atsBusinessDays) },
        collSndDate: { minDate: addBusinessDays(now, 5) },
        collStartDate: { minDate: addBusinessDays(now, 4), maxDate: getDatePart(collectionDeadline), maxDateTime: collectionDeadline },
        collEndDate: { minDate: addBusinessDays(now, 4), maxDate: getDatePart(collectionDeadline), maxDateTime: collectionDeadline },
    };
}

// 셀프 메시지에서 일정 관련 필드를 변경하고 필요한 연관 일정을 보정한다.
export function applySelfScheduleChange(schedule, field, value, { isRemind = false, now = new Date() } = {}) {
    const nextSchedule = { ...schedule };

    switch (field) {
        case 'targetType':
            return createSelfDefaultSchedule({
                isRemind,
                targetType: value,
                mapticsSendType: schedule.mapticsSendType,
                now,
            });
        case 'mapticsSendType':
            nextSchedule.mapticsSendType = value;
            return nextSchedule;
        case 'atsSndStartDate':
            nextSchedule[field] = value;
            return nextSchedule;
        case 'collEndDate':
            nextSchedule.collEndDate = value;
            return schedule.mapticsSendType === 'maptics_collect'
                ? normalizeCollectionSchedule(nextSchedule)
                : nextSchedule;
        case 'collSndDate': {
            nextSchedule.collSndDate = value;
            return normalizeCollectionSchedule(nextSchedule);
        }
        case 'collStartDate':
            nextSchedule.collStartDate = value;
            if (schedule.mapticsSendType === 'maptics_collect') {
                return normalizeCollectionSchedule(nextSchedule);
            }
            if (schedule.mapticsSendType === 'maptics_realtime') {
                nextSchedule.collEndDate = value;
            }
            return nextSchedule;
        case 'rtEndHour':
            nextSchedule.rtEndHour = value;
            nextSchedule.collEndDate = `${getDatePart(schedule.collStartDate)} ${value}:${schedule.rtEndMinute}`;
            return nextSchedule;
        case 'rtEndMinute':
            nextSchedule.rtEndMinute = value;
            nextSchedule.collEndDate = `${getDatePart(schedule.collStartDate)} ${schedule.rtEndHour}:${value}`;
            return nextSchedule;
        default:
            return nextSchedule;
    }
}
